/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine} from '@xstate/react';
import {faServer} from '@fortawesome/free-solid-svg-icons';

import {
  Alert,
  ContentBody,
  FieldWrapper,
  NxButton,
  NxLoadWrapper,
  NxSubmitMask,
  NxTooltip,
  Page,
  PageHeader,
  PageTitle,
  Section,
  SectionFooter,
  Textarea,
  Textfield,
  Utils
} from '@sonatype/nexus-ui-plugin';

import DataStoreConfigurationMachine from './DataStoreConfigurationMachine';
import UIStrings from '../../../../constants/UIStrings';

const FIELDS = UIStrings.DATASTORE_CONFIGURATION.FIELDS;

export default function DataStoreConfiguration() {
  const [current, send] = useMachine(DataStoreConfigurationMachine, {devTools: true});

  const {data : settings, isPristine, validationErrors, saveError, loadError} = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const isInvalid = Utils.isInvalid(validationErrors);
  const errorMessage = saveError;
  const hasData = settings && settings !== {};

  function updateSettings({target}) {
    send('UPDATE', {
      data: {
        [target.name]: target.value
      }
    });
  }

  function saveSettings(event) {
    event.preventDefault();
    send('SAVE');
  }

  function discardChanges() {
    send('RESET');
  }

  function handleEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      send('SAVE');
    }
  }

  function retry() {
    send('RETRY');
  }

  let error = null;
  if (errorMessage instanceof Array) {
    error = <Alert type="error">
      {UIStrings.DATASTORE_CONFIGURATION.MESSAGES.SAVE_ERROR}
      <ul>
        {errorMessage.map(error => <li key={error.id}>{JSON.stringify(error)}</li>)}
      </ul>
    </Alert>;
  }
  else if (errorMessage) {
    error = <Alert type="error">
      {UIStrings.DATASTORE_CONFIGURATION.MESSAGES.SAVE_ERROR}<br/>
      {errorMessage}
    </Alert>;
  }

  return <Page>
    <PageHeader><PageTitle icon={faServer} {...UIStrings.DATASTORE_CONFIGURATION.MENU}/></PageHeader>
    <ContentBody className='nxrm-datastore-configuration'>
      <Section>
      <NxLoadWrapper loading={isLoading} error={loadError ? `${loadError}` : null} retryHandler={retry}>
        {hasData && <>
          {isSaving && <NxSubmitMask message={UIStrings.SAVING}/>}
          {error}

          <FieldWrapper labelText={FIELDS.jdbcUrlLabel}
                        descriptionText={FIELDS.jdbcUrlDescription}
                        id='nxrm-datastore-jdbc-url'>
            <Textfield
                className="nx-text-input--long"
                {...Utils.fieldProps('jdbcUrl', current)}
                disabled="diabled"
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.usernameLabel}
                        descriptionText={FIELDS.usernameDescription}
                        id='nxrm-datastore-username'>
            <Textfield
                {...Utils.fieldProps('username', current)}
                disabled="diabled"
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.schemaLabel}
                        descriptionText={FIELDS.schemaDescription}
                        id='nxrm-datastore-schema'>
            <Textfield
                {...Utils.fieldProps('schema', current)}
                disabled="diabled"
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.maxConnectionPoolLabel}
                        descriptionText={FIELDS.maxConnectionPoolDescription}
                        id='nxrm-datastore-max-connection-pool'>
            <Textfield
                {...Utils.fieldProps('maximumConnectionPool', current)}
                onChange={updateSettings}
                onKeyPress={handleEnter}
                type="number"
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.advancedLabel}
                        id='nxrm-datastore-advanced'>
            <Textarea
                className="nx-text-input--long"
                {...Utils.fieldProps('advanced', current)}
                onChange={updateSettings}
            />
          </FieldWrapper>

          <SectionFooter>
            <NxTooltip title={Utils.saveTooltip({isPristine, isInvalid})}>
              <NxButton variant="primary"
                        className={(isPristine || isInvalid) && 'disabled'}
                        onClick={saveSettings} type="submit">
                {UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
              </NxButton>
            </NxTooltip>
            <NxTooltip title={Utils.discardTooltip({isPristine})}>
              <NxButton className={isPristine && 'disabled'}
                        onClick={discardChanges}>
                {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
              </NxButton>
            </NxTooltip>
          </SectionFooter>
        </>}
        </NxLoadWrapper>
      </Section>
    </ContentBody>
  </Page>;
}
